import { useCallback } from 'react'
import { toastManager } from './toast-store'
import { ToastOptions } from './types'
import { ToastContext } from './components/toast/nested/context'
import { ToastContent } from './components/toast/nested/content'
import { ToastHeader } from './components/toast/nested/header'
import { ToastTitle } from './components/toast/nested/title'
import { ToastDescription } from './components/toast/nested/description'
import { ToastIcon, ToastHighlightIcon } from './components/toast/nested/icon'
import { ToastActions } from './components/toast/nested/action'
import { NotchToastIconProvider } from './notch-toast-icon'

const DEFAULT_DURATION = 7000

function resolveDuration({ duration, persistent }: ToastOptions): number | null {
  if (persistent || duration === Infinity) return null
  return duration ?? DEFAULT_DURATION
}

export function useToast() {
  const add = useCallback((options: ToastOptions) => {
    let resolvedId = options.id ?? ''
    const NotchWrapper = options.notchBody
      ? () => {
          const notchBody = options.notchBody
          if (!notchBody) {
            return null
          }
          return <NotchToastIconProvider icon={options.icon}>{notchBody(resolvedId)}</NotchToastIconProvider>
        }
      : null
    const notchDescription = NotchWrapper ? <NotchWrapper /> : undefined
    const notchRowFn = options.notchRow
    const NotchRow = notchRowFn ? () => notchRowFn(resolvedId) : null
    const notchRow = NotchRow ? <NotchRow /> : undefined

    // When body is provided, render via custom path with Interactive wrapper
    if (options.body) {
      const bodyFn = options.body
      const Wrapper = () => {
        const content = bodyFn(resolvedId)
        return content
      }
      resolvedId = toastManager.show({
        id: options.id,
        description: <Wrapper />,
        notchDescription,
        notchRow,
        icon: options.icon ?? null,
        title: options.title,
        headerExtra: options.headerExtra,
        duration: resolveDuration(options),
        persistent: options.persistent,
        roundness: 12,
        styles: options.styles,
        autoExpandDelayMs: options.autoExpandDelayMs,
        autoCollapseDelayMs: options.autoCollapseDelayMs,
        keepExpandedOnRefresh: options.keepExpandedOnRefresh,
        stableHeaderKey: options.stableHeaderKey,
        priority: options.priority,
        onDismiss: options.onDismiss,
        onHeaderClick: options.onHeaderClick,
        analytics: options.analytics,
      })
      return resolvedId
    }

    // When actions are present, render via custom path so our compound components display them
    if (options.actions && options.actions.length > 0) {
      let toastId = ''
      const Wrapper = () => {
        const close = () => toastManager.dismiss(toastId)
        return (
          <ToastContext.Provider value={{ toastId, close }}>
            <div data-slot="Toast" className="group relative">
              <ToastContent>
                <ToastHeader>
                  <ToastIcon>{options.icon ?? <ToastHighlightIcon />}</ToastIcon>
                  {options.title && <ToastTitle>{options.title}</ToastTitle>}
                </ToastHeader>
                {options.description && <ToastDescription>{options.description}</ToastDescription>}
                <ToastActions actions={options.actions} />
              </ToastContent>
            </div>
          </ToastContext.Provider>
        )
      }
      toastId = toastManager.show({
        id: options.id,
        description: <Wrapper />,
        notchDescription,
        notchRow,
        descriptionHasHeader: true,
        icon: options.icon ?? null,
        title: options.title,
        headerExtra: options.headerExtra,
        duration: resolveDuration(options),
        persistent: options.persistent,
        roundness: 12,
        styles: options.styles,
        autoExpandDelayMs: options.autoExpandDelayMs,
        autoCollapseDelayMs: options.autoCollapseDelayMs,
        keepExpandedOnRefresh: options.keepExpandedOnRefresh,
        stableHeaderKey: options.stableHeaderKey,
        priority: options.priority,
        onDismiss: options.onDismiss,
        onHeaderClick: options.onHeaderClick,
        analytics: options.analytics,
      })
      resolvedId = toastId
      return resolvedId
    }

    resolvedId = toastManager.show({
      id: options.id,
      title: options.title,
      description: options.description,
      notchDescription,
      notchRow,
      icon: options.icon ?? null,
      headerExtra: options.headerExtra,
      duration: resolveDuration(options),
      persistent: options.persistent,
      roundness: 12,
      styles: options.styles,
      autoExpandDelayMs: options.autoExpandDelayMs,
      autoCollapseDelayMs: options.autoCollapseDelayMs,
      keepExpandedOnRefresh: options.keepExpandedOnRefresh,
      stableHeaderKey: options.stableHeaderKey,
      priority: options.priority,
      onDismiss: options.onDismiss,
      onHeaderClick: options.onHeaderClick,
      analytics: options.analytics,
    })
    return resolvedId
  }, [])

  const dismiss = useCallback((id: string) => {
    toastManager.dismiss(id)
  }, [])

  return { add, dismiss }
}
